import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { message } from 'antd';
import { Detail, Prompt } from 'components/Page';
import {
  add, clear, dirty, init, list, load, reset, update
} from 'actions';

let form = React.createRef();
/**
 * @description Map data and server flight
 * @param {state} Object
 */
const mapStateToProps = (state, ownProps) => ({
  item: state.api[ownProps.reducer.item.key].data,
  formInFlight: state.api[ownProps.reducer.item.key].inFlight,
  formError: state.api[ownProps.reducer.item.key].error
});

/**
 * @description Dispatch create, loadList, load, deleteItem
 * @param {dispatch} Object
 */
const mapDispatchToProps = (dispatch, ownProps) => ({
  create() {
    dispatch(init(ownProps.reducer.item.key, { active: true, ...ownProps.config.init }));
  },
  clearErrors() {
    dispatch(clear(ownProps.reducer.item.key));
  },
  resetItem() {
    if (ownProps.reducer.extra) {
      dispatch(reset(ownProps.reducer.extra.key));
    }
    return dispatch(reset(ownProps.reducer.item.key));
  },
  loadList(params) {
    dispatch(list(
      ownProps.reducer.list.key, params
    ));
  },
  loadItem(data) {
    const { id } = data;
    return dispatch(load(ownProps.reducer.item.key, { id }));
  },
  reloadList() {
    dispatch(dirty(ownProps.reducer.list.key));
  },
  saveItem(id, data) {
    return dispatch(id > 0 ? update(ownProps.reducer.item.key, { id, ...data }) : add(ownProps.reducer.item.key, data));
  }
});
/**
 * @description Launch the Prompt to Save or Discard changes
 * @param {save} Function
 * @param {cancel} Function
 */
const promptSaveChanges = (save, cancel) => (
  Prompt({
    onOkClick: save,
    onCancelClick: cancel
  })
);
/**
 * LeaveTypes
 * @description Leave Type View
 * @version 1.0.0
 */
const Tabbed = ({
  canAdmin,
  classes,
  config,
  clearErrors,
  formError,
  formInFlight,
  item,
  loadItem,
  messaging,
  onPrepareData,
  reloadList,
  resetItem,
  saveItem,
  themeName,
  ...rest
}) => {
  /*
   ** @description Hook into the lifecycle to get latest id
   */
  useEffect(() => {
    loadItem(config.parent);
  }, [config]);
  /**
   * @description Save the item and dislpay a relevant message
   * @param {id} Number
   * @param {data} Object
   * @param {prevData} Object
   * @triggers {reloadList}
   */
  const handleSave = (id, data, prevData = null) => {
    const preparedData = onPrepareData(data);
    return saveItem(id, preparedData)
      .then((response) => {
        message.success(messaging.save.success(data));
        if (prevData) {
          resetItem();
          loadItem(prevData); // load previous data
        }
        reloadList(); // reload change list data
        return response;
      },
      (error) => {
        message.error(messaging.save.failure(data));
        return error;
      });
  };
  /**
   * @description Check if the form is dirty
   * @triggers {promptSaveChanges}
   */
  const handleFormClose = (currentItem, previousItem) => {
    // Prompt to save the changed form data
    promptSaveChanges(() => handleSave(previousItem.id, previousItem, currentItem),
      () => {
      });
  };

  /**
   * @description Check if the form is dirty
   * @triggers {promptSaveChanges}
   */
  const handleTabChange = () => {
    const { currentItem, isFormDirty } = form.state;
    // if the the form is dirty present a prompt to save
    // the form data
    if (isFormDirty) {
      form.state.isFormDirty = false; // reset form to not dirty
      promptSaveChanges(() => handleSave(currentItem.id, currentItem),
        () => loadItem(currentItem)); // reset to origin datasource
    }
  };

  /**
   * @description update form reference
   */
  const getFormRef = (input) => {
    form = input || form;
  };

  // Is the form in a state of edit or not
  const isEditMode = (item.id !== null && item.id > 0) || false;

  // Layout Component to build out the Page
  return (
    <div className={classNames(classes, 'bhp-container')}>
      <Detail
        {...config}
        currentItem={item}
        isEditMode
        onTabChange={handleTabChange}
        tabPosition="left"
        tabItems={config.tabItems.map((tab) => {
          const {
            key, title, component, closeOnSave
          } = tab;
          // setup props obejct
          const props = {
            canAdmin,
            closeOnSave,
            commands: {
              ...config.commands
            },
            dataSource: item,
            formError,
            inFlight: formInFlight,
            isEditMode,
            itemId: item.id, // backwards compatiblity
            objectId: item.id,
            onSaveClick: handleSave,
            onFormClose: handleFormClose,
            onCancelClick: () => {
              clearErrors();
              resetItem();
            },
            themeName,
            ...rest
          };
          props.ref = !key ? getFormRef : undefined; // Main form needs a ref
          return ({
            key,
            title,
            component: component(props)
          });
        })}
      />
    </div>
  );
};

Tabbed.propTypes = {
  canAdmin: PropTypes.bool,
  classes: PropTypes.string,
  config: PropTypes.object,
  clearErrors: PropTypes.func,
  formError: PropTypes.object,
  formInFlight: PropTypes.bool,
  item: PropTypes.object,
  loadItem: PropTypes.func,
  messaging: PropTypes.object,
  onPrepareData: PropTypes.func,
  resetItem: PropTypes.func,
  saveItem: PropTypes.func,
  reloadList: PropTypes.func,
  themeName: PropTypes.string
};

Tabbed.defaultProps = {
  canAdmin: false,
  classes: '',
  clearErrors() {
  },
  config: {},
  formError: {},
  formInFlight: false,
  item: {},
  loadItem() {
  },
  messaging: {
    save: {
      failure: data => (data.name ? `Saving '${data.name}' failed` : 'Saving Failed'),
      success: data => (data.name ? `Saving '${data.name}' was successful` : 'Saving successful')
    }
  },
  onPrepareData(data) {
    return data;
  },
  resetItem() {
  },
  saveItem() {
  },
  reloadList() {
  },
  themeName: ''
};

export default connect(mapStateToProps, mapDispatchToProps)(Tabbed);
